/**
 * Budget JSON Field Configurations
 *
 * This file defines all fields that should be stored in the budget_json column
 * for each platform and budget type. It serves as the single source of truth
 * for dynamic field management.
 *
 * USAGE:
 * - Add new fields here with their configuration
 * - The budgetJsonUtils.js will handle serialization/deserialization automatically
 * - No need to manually update GroupEditModal mapping logic
 */

/**
 * Field Configuration Schema:
 *
 * {
 *   name: string,              // Form field name (e.g., 'googleLanguages')
 *   budgetJsonPath: string,    // Path in budget_json (e.g., 'languages' or 'geo.countries')
 *   type: string,              // Field type: 'text', 'select', 'multiselect', 'textarea', 'number'
 *   label: string,             // Display label
 *   tooltip: string,           // Optional tooltip text
 *   required: boolean,         // Whether field is required
 *   format: string,            // How to transform value:
 *                              //   - 'csv_array': "a,b,c" -> ["a", "b", "c"]
 *                              //   - 'select_value': {label, value} -> value
 *                              //   - 'select_to_array': {label, value} -> [value]
 *                              //   - 'multiselect_values': [{label, value}, ...] -> [value, ...]
 *                              //   - 'number': "123" -> 123
 *   defaultValue: any,         // Default value for new budgets
 *   showInSetupFlow: array,    // ['quick', 'advanced'] or null for all
 *   showForBudgetTypes: array, // ['google_search', ...] or null for all
 * }
 */

export const BUDGET_JSON_FIELD_CONFIGS = {
  // ============================================================================
  // GOOGLE PLATFORM
  // ============================================================================
  google: {
    fields: [
      // Brand Name - Optional field for all Google ad types
      {
        name: 'googleBrandName',
        budgetJsonPath: 'brandName',
        type: 'text',
        label: 'Brand Name (Optional)',
        tooltip: 'Enter the brand name associated with this budget',
        required: false,
        format: null, // Plain text, no transformation
        showInSetupFlow: null, // Show in all flows
        showForBudgetTypes: null, // Show for all Google budget types
      },

      // Sales Country - Required for all Google ad types
      {
        name: 'googleSalesCountry',
        budgetJsonPath: 'salesCountry',
        type: 'select',
        label: 'Sales Country',
        tooltip: 'Select the primary country where sales will be attributed',
        required: true,
        format: 'select_value', // {label, value} -> value
        defaultValue: 'US',
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Language Constants - Required for all Google ad types
      {
        name: 'googleLanguages',
        budgetJsonPath: 'languages',
        type: 'textarea',
        label: 'Language Constants (Required)',
        tooltip: 'Enter comma-separated Google language constants',
        required: true,
        format: 'csv_array', // "lang1, lang2" -> ["lang1", "lang2"]
        defaultValue: ['languageConstants/1000'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: States
      {
        name: 'geoStates',
        budgetJsonPath: 'geo.states',
        type: 'textarea',
        label: 'Target States',
        tooltip: 'Enter comma-separated state codes or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Cities
      {
        name: 'geoCities',
        budgetJsonPath: 'geo.cities',
        type: 'textarea',
        label: 'Target Cities',
        tooltip: 'Enter comma-separated city names or constants',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Postal Codes
      {
        name: 'geoPostalCodes',
        budgetJsonPath: 'geo.postalCodes',
        type: 'textarea',
        label: 'Target Postal Codes',
        tooltip: 'Enter comma-separated postal codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },
    ],
  },

  // ============================================================================
  // META PLATFORM
  // ============================================================================
  meta: {
    fields: [
      // Languages - Multi-select for Meta
      {
        name: 'metaLanguages',
        budgetJsonPath: 'languages',
        type: 'multiselect',
        label: 'Languages',
        tooltip: 'Select the languages to target for your Meta ads',
        required: false,
        format: 'multiselect_values', // [{label, value}, ...] -> [value, ...]
        defaultValue: ['en_US'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries (Meta only supports countries)
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Gender Targeting
      {
        name: 'metaGender',
        budgetJsonPath: 'meta.targeting.genders',
        type: 'select',
        label: 'Gender Targeting',
        tooltip: 'Select the gender targeting for your Meta ads',
        required: false,
        format: 'select_to_array', // {label, value} -> [value]
        defaultValue: [0], // All genders
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Age Min
      {
        name: 'metaAgeMin',
        budgetJsonPath: 'meta.targeting.age_min',
        type: 'number',
        label: 'Min Age',
        tooltip: 'Minimum age for audience targeting (13-65)',
        required: false,
        format: 'number', // "25" -> 25
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },

      // Age Max
      {
        name: 'metaAgeMax',
        budgetJsonPath: 'meta.targeting.age_max',
        type: 'number',
        label: 'Max Age',
        tooltip: 'Maximum age for audience targeting (13-65)',
        required: false,
        format: 'number',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },
    ],
  },

  // ============================================================================
  // TIKTOK PLATFORM
  // ============================================================================
  tiktok: {
    fields: [
      // Languages - Multi-select for TikTok
      {
        name: 'tiktokLanguages',
        budgetJsonPath: 'languages',
        type: 'multiselect',
        label: 'Languages',
        tooltip: 'Select the languages to target for your TikTok ads',
        required: false,
        format: 'multiselect_values',
        defaultValue: ['en_US'],
        showInSetupFlow: null,
        showForBudgetTypes: null,
      },

      // Geo-Targeting: Countries
      {
        name: 'geoCountries',
        budgetJsonPath: 'geo.countries',
        type: 'textarea',
        label: 'Target Countries',
        tooltip: 'Enter comma-separated country codes',
        required: false,
        format: 'csv_array',
        showInSetupFlow: ['advanced'],
        showForBudgetTypes: null,
      },
    ],
  },

  // ============================================================================
  // AMAZON PLATFORM - DSP-SPECIFIC FIELDS
  // ============================================================================
  amazon: {
    fields: [
      // DSP Gender (stored in budget_json.dsp.gender)
      {
        name: 'dspGender',
        budgetJsonPath: 'dsp.gender',
        type: 'select',
        label: 'Gender Targeting',
        tooltip: 'Select gender targeting for DSP ads',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'], // Only for DSP budget type
      },

      // DSP Ad Format
      {
        name: 'dspAdFormat',
        budgetJsonPath: 'dsp.ad_format',
        type: 'select',
        label: 'Ad Format',
        tooltip: 'Select the ad format for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },

      // DSP Spend Tactic
      {
        name: 'dspSpendTactic',
        budgetJsonPath: 'dsp.spend_tactic',
        type: 'select',
        label: 'Spend Tactic',
        tooltip: 'Select the spend tactic for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },

      // DSP Spend Speed
      {
        name: 'dspSpendSpeed',
        budgetJsonPath: 'dsp.spend_speed',
        type: 'select',
        label: 'Spend Speed',
        tooltip: 'Select the spend speed for DSP campaigns',
        required: false,
        format: 'select_value',
        showInSetupFlow: null,
        showForBudgetTypes: ['dsp'],
      },
    ],
  },

  // ============================================================================
  // WALMART PLATFORM
  // ============================================================================
  walmart: {
    fields: [
      // Currently no walmart-specific budget_json fields
      // Add fields here as needed
    ],
  },
};

/**
 * Get field configuration for a specific platform and budget type
 * @param {string} platform - Platform key (e.g., 'google', 'meta')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp')
 * @param {string} setupFlow - Setup flow ('quick' or 'advanced')
 * @returns {array} Array of field configurations that should be displayed
 */
export function getFieldsForContext(platform, budgetType, setupFlow) {
  const platformConfig = BUDGET_JSON_FIELD_CONFIGS[platform];
  if (!platformConfig || !platformConfig.fields) {
    return [];
  }

  return platformConfig.fields.filter(field => {
    // Check setupFlow filter
    if (field.showInSetupFlow && !field.showInSetupFlow.includes(setupFlow)) {
      return false;
    }

    // Check budgetType filter
    if (field.showForBudgetTypes && !field.showForBudgetTypes.includes(budgetType)) {
      return false;
    }

    return true;
  });
}

/**
 * Get all fields for a platform (regardless of context)
 * @param {string} platform - Platform key
 * @returns {array} Array of all field configurations for the platform
 */
export function getAllFieldsForPlatform(platform) {
  const platformConfig = BUDGET_JSON_FIELD_CONFIGS[platform];
  if (!platformConfig || !platformConfig.fields) {
    return [];
  }
  return platformConfig.fields;
}
